import React, {useRef, useState} from 'react';
import AppMenu from '../../../../../../../components/common/AppMenu';
import {SVG} from '../../../../../../../constants/assetLists';
import {useReactToPrint} from 'react-to-print';
import AppPrintTable, {
  PrintColumn,
} from '../../../../../../../components/common/AppPrintTable';
import AppConfirmationDialog from '../../../../../../../components/common/AppConfirmationDialog';
import {useUsersContext} from '../../../../../../../context/users/UsersContextProvider';
import {UserItem} from '../../../users';
import moment from 'moment';
import {exportToExcelFromColumns} from '../../../../../../../utils/exportToExcel';
import {useClientsContext} from '../../../../../../../context/clients/ClientsContextProvider';
import {getRoleTypeName} from '../../../../../../../utils';

const COLUMNS: Array<PrintColumn<UserItem>> = [
  {
    title: 'שם משתמש',
    accessor: r => r?.firstName + ' ' + r?.lastName,
    width: 160,
  },
  {title: 'אימייל', accessor: r => r?.email || '-', width: 220},
  {title: 'טלפון', accessor: r => r?.phone || '-', width: 130},
  {
    title: 'תפקיד',
    accessor: r => getRoleTypeName(r?.roles[0]?.roleType) || '-',
    width: 140,
  },
  {
    title: 'סטאטוס חשבון',
    accessor: r => (r.status === 'active' ? 'פעיל' : 'לא פעיל'),
    width: 120,
  },
  {
    title: 'ארגונים',
    accessor: r => r?.clientId?.map(c => c.name).join(', ') || '-',
    width: 150,
  },
  {
    title: 'סניפים',
    accessor: r => r?.branchId?.map(b => b.name).join(', ') || '-',
    width: 150,
  },
  {
    title: 'תתי-סניפים',
    accessor: r => r?.subBranchId?.map(sb => sb.name).join(', ') || '-',
    width: 150,
  },
  {
    title: 'תאריך יצירה',
    accessor: r => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
    width: 140,
  },
  {
    title: 'משוייך לקבוצה',
    accessor: r =>
      r?.associatedGroups?.map(g => g.groupName)?.join(', ') || '-',
    width: 160,
  },
];

const MENUITEMS = [
  {
    title: 'יצוא רשימה לאקסל',
    value: 'export',
    icon: SVG.csvGreenIcon,
  },
  {
    title: 'הדפסת רשימה',
    value: 'print',
    icon: SVG.printerBlackIcon,
  },
  {
    title: 'הצגת ארכיון (מוסיף משתמשים לא פעילים לטבלה)',
    value: 'archived',
    icon: 'checkbox',
  },
];

function MenuSection() {
  const {selectedUsers, allUsers, menuSelectedItem, setMenuSelectedItem} =
    useUsersContext();
  const {selectedLocation} = useClientsContext();

  const [anchorTaskEl, setAnchorTaskEl] = useState<HTMLElement | null>(null);
  const [showPrintConfirmationDialog, setShowPrintConfirmationDialog] =
    useState(false);

  const printRef = useRef<HTMLDivElement>(null);

  const rowsToPrint =
    selectedUsers?.length > 0
      ? allUsers?.data?.filter(u => selectedUsers.includes(u?._id))
      : allUsers?.data;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Users',
  });

  const handleItemClick = (val: string) => {
    if (val === 'print') {
      setShowPrintConfirmationDialog(true);
    } else if (val === 'export') {
      const clientName =
        rowsToPrint && rowsToPrint.length > 0 && rowsToPrint[0]?.clientId
          ? rowsToPrint[0]?.clientId
              ?.map(c => c.name)
              .join(' ')
              ?.replaceAll(' ', '_')
          : 'Users';
      const branchName =
        selectedLocation && !selectedLocation?.isClient
          ? selectedLocation?.name?.replaceAll(' ', '_')
          : null;
      const fileName = branchName
        ? `Users_List_${clientName}_${branchName}.xlsx`
        : `Users_List_${clientName}.xlsx`;
      exportToExcelFromColumns(COLUMNS, rowsToPrint ?? [], fileName);
    } else if (val === 'archived') {
      setMenuSelectedItem(prev => {
        if (prev === 'archived') {
          return '';
        } else {
          return val;
        }
      });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorTaskEl(event.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => {
    setAnchorTaskEl(null);
  };

  const handleOnConfirm = () => {
    setShowPrintConfirmationDialog(false);
    handlePrint();
  };

  const handleConfirmationClose = () => {
    setShowPrintConfirmationDialog(false);
  };

  return (
    <>
      <AppMenu
        anchorEl={anchorTaskEl}
        showDivider
        dividerIndex={1}
        itemIconWidth={24}
        itemIconHeight={24}
        open={Boolean(anchorTaskEl)}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        items={MENUITEMS}
        onItemClick={val => handleItemClick(val)}
        selectedItem={menuSelectedItem}
      />

      <div style={{display: 'none'}}>
        <AppPrintTable<UserItem>
          ref={printRef}
          title="רשימת משתמשים"
          subtitle={
            selectedUsers?.length > 0
              ? `מספר משתמשים מסומנים: ${selectedUsers?.length || 0}`
              : `סה״כ משתמשים: ${allUsers?.data?.length || 0}`
          }
          columns={COLUMNS}
          rows={rowsToPrint as UserItem[]}
        />
      </div>

      <AppConfirmationDialog
        open={showPrintConfirmationDialog}
        onClose={handleConfirmationClose}
        onConfirm={handleOnConfirm}
        icon={
          <img
            src={SVG.printerAquaLgIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="הדפסה"
        subTitle="שליחה להדפסה?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default MenuSection;
